import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { showToastMessage } from "@allaria-tech/matter/ToastMessage";
import { useAppDispatch } from "../app/hooks";
import { OnboardingType } from "../types";
import { genericToastifyError } from "../utils";
import { updateToken } from "../reducers/authReducer";
import { setIsMobileLayout, setPlatform } from "../reducers/cryptoReducer";
import {
  allariaAllowedOrigins,
  getIsMobileLayoutFromUrl,
  getMissingAllariaEnvVars,
  getPlatformFromUrl,
  postToParent,
  startReportingModalHeight,
  subscribeToParentToken,
  takeOverEarlyHandshake,
} from "../utils/crypto";

/**
 * Keeps the channel with the Allaria app open for the whole onboarding, and takes
 * over the handshake started in main.tsx: it declares the platform, adopts the
 * token the app already answered with and stores every one that follows.
 *
 * This used to live inside CryptoAccountStep, then step 1. Once the user
 * moved on, the component unmounted and the listener was torn down, so a token
 * pushed later — after a 401, or when the app refreshed it — had nobody
 * listening. The liveness is both the last and the slowest part of the flow, so
 * that is exactly where an expired token is most likely and where there was no
 * way to replace it.
 */
const useCryptoBridge = ({
  onboardingType,
  isLoading,
}: {
  onboardingType: OnboardingType;
  isLoading: boolean;
}) => {
  const dispatch = useAppDispatch();

  // The modal starts at the first step's height: reporting the spinner's while
  // the template loads would shrink it for a moment, only to grow it back.
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (!isLoading) setHasLoaded(true);
  }, [isLoading]);

  // A build without the app's origins can never get the token. Reported as
  // soon as the flow opens, not after the user went through it.
  useEffect(() => {
    if (onboardingType !== "DIGITAL") return;

    const missingVars = getMissingAllariaEnvVars();
    if (!missingVars.length) return;

    const error = new Error(`${missingVars.join(", ")} is not defined`);
    console.error(error);
    Sentry.captureException(error);
    showToastMessage(genericToastifyError, {
      containerId: "layoutPage",
      type: "ERROR",
    });
  }, [onboardingType]);

  useEffect(() => {
    // No allowed origin means no handshake is possible; the effect above
    // reports the missing env vars.
    if (onboardingType !== "DIGITAL" || !allariaAllowedOrigins.length) return;

    const unsubscribe = subscribeToParentToken((token) =>
      dispatch(updateToken(token)),
    );

    // Dispatched before anything is sent: postToParent resolves the target origin
    // from the platform in the store, and dispatch is synchronous.
    dispatch(setPlatform(getPlatformFromUrl()));
    const isMobileLayout = getIsMobileLayoutFromUrl();
    dispatch(setIsMobileLayout(isMobileLayout));

    // READY was already announced from main.tsx, long before this tree mounted.
    // If the app answered in the meantime the token was buffered — the store
    // could not be written to yet — so it is adopted here. If it did not, the
    // app was not listening in time and is asked again.
    const earlyToken = takeOverEarlyHandshake();

    if (earlyToken) dispatch(updateToken(earlyToken));
    else postToParent("DIGITAL_ONBOARDING_READY");

    return unsubscribe;
  }, [onboardingType, dispatch]);

  useEffect(() => {
    if (
      onboardingType !== "DIGITAL" ||
      !allariaAllowedOrigins.length ||
      !hasLoaded
    )
      return;

    return startReportingModalHeight(getIsMobileLayoutFromUrl());
  }, [onboardingType, hasLoaded]);
};

export default useCryptoBridge;
